import { useId, useState } from 'react';
import { ClipboardRefusedError, copyTextWhenReady } from '../general/clipboard';
import {
    Calendar as CalendarIcon,
    Check,
    ChevronsUpDown,
    Copy,
    Filter,
    Info,
    Link2,
    Loader2,
    Mail,
    Plus,
    Trash2,
} from 'lucide-react';

import { MarketplaceOffer, getEntityType } from '@the-real-insight/tri-model';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { usePlanDialog } from './hooks/usePlanDialog';
import {
    additionalFeeLabel,
    billingModelLabel,
    billingModelOptions as billingModelOptionsFor,
    includedLabel,
    isUsageMeter,
    minimumIncluded,
} from './billingModels';
import { userAPI } from '../UserAPI';
import { useModalContext } from '../components/ModalProvider';
import { useAtom } from 'jotai';
import { authenticationAtom } from '../State';

import type { ColumnDef } from '@tanstack/react-table';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { DataTable } from '@/components/ui/data-table';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';

interface PlanPanelProps {
    type: any
    marketplaceOffer: MarketplaceOffer;
    setMarketplaceOffer: (marketplaceOffer: MarketplaceOffer) => void;
}

interface NumberFieldProps {
    label: string;
    value: number | undefined;
    /** Renders a text input so a decimal separator ("7." / "7,5") survives typing. */
    decimal?: boolean;
    /** Decimal places rendered while the field is not being edited. */
    fixedDecimals?: number;
    min?: number;
    step?: number;
    suffix?: string;
    onChange: (event: any, value: string) => void;
}

/** Spaltenkopf der Plantabelle — die abgelöste Tabelle sortierte hier nicht. */
const plainHeader = (text: string) => () => <span className="font-bold text-foreground">{text}</span>;

/** Rechtsbündiger Spaltenkopf — entspricht den rechtsbündigen Zahlenspalten. */
const rightHeader = (text: string) => () => <div className="w-full text-right font-bold text-foreground">{text}</div>;

/**
 * Numeric input that keeps the user's raw keystrokes while they type.
 *
 * Binding a parsed number straight back into the input breaks entry: the
 * intermediate strings "" and "7." are not valid numbers, so re-serialising the
 * parsed value on every render overwrites what was typed (and `.toFixed()` even
 * re-rounds it). We keep the edit buffer as a string, push a value up only once
 * it parses, and re-format on blur. Decimal fields use a text input so that a
 * trailing separator — and the German comma — survive.
 */
function NumberField({ label, value, decimal, fixedDecimals, min, step, suffix, onChange }: NumberFieldProps) {
    const fieldId = useId();
    const [draft, setDraft] = useState<string | null>(null);

    const formatted = value == null || Number.isNaN(value)
        ? ''
        : fixedDecimals != null ? value.toFixed(fixedDecimals) : String(value);

    return (
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Label htmlFor={fieldId}>{label}</Label>
            <div className="relative">
                <Input
                    id={fieldId}
                    type={decimal ? 'text' : 'number'}
                    min={decimal ? undefined : min}
                    step={decimal ? undefined : step}
                    className={cn(suffix && 'pr-8')}
                    value={draft ?? formatted}
                    onChange={(event) => {
                        const next = event.target.value ?? '';
                        setDraft(next);

                        const normalized = next.replace(',', '.').trim();
                        // Keep an emptied or half-typed field as-is; commit once it parses.
                        if (normalized === '' || !Number.isFinite(Number(normalized))) return;
                        onChange(event, normalized);
                    }}
                    onBlur={() => setDraft(null)}
                />
                {suffix ? (
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                        {suffix}
                    </span>
                ) : null}
            </div>
        </div>
    );
}

// Helper to check if input is an email format
const isEmailFormat = (input: string): boolean => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(input);
};

export default function PlanPanel({
    type,
    marketplaceOffer,
    setMarketplaceOffer,
}: PlanPanelProps) {
    const { t } = useTranslation();
    const fieldId = useId();
    const { pushError, showMessageDialog } = useModalContext() as any;
    const [authentication] = useAtom(authenticationAtom) as any;

    // State for inline voucher actions (track which plan is being processed)
    const [loadingVoucherForPlan, setLoadingVoucherForPlan] = useState<string | null>(null);

    // Filter state
    const [filterText, setFilterText] = useState('');
    /** Nur Anzeigezustand der Abrechnungsmodell-Auswahl (Popover offen/zu). */
    const [billingModelOpen, setBillingModelOpen] = useState(false);
    /** Nur Anzeigezustand des Kalenders für „gültig ab“. */
    const [validFromOpen, setValidFromOpen] = useState(false);
    const {
        planDialogOpen,
        selectedPlan,
        openPlanDialog,
        closePlanDialog,
        savePlan,
        handleNameChange,
        handleDescriptionChange,
        handleBaseFeeChange,
        handleIncludedRequestsChange,
        handleFeePerAdditionalRequestChange,
        handleIncludedUsersChange,
        handleMonthlyFeeChange,
        handleTermChange: _handleTermChange,
        handleValidFromChange,
        handleBillingModelChange,
        customerCode,
        customerCodeError,
        customerDetails,
        handleCustomerCodeChange,
        // Voucher dialog
        voucherDialogOpen,
        voucher,
        savedPlanForVoucher,
        closeVoucherDialog,
        handleCopyVoucher,
        handleCopyVoucherLink,
        handleEmailVoucher,
        loadingVoucher,
    } = usePlanDialog({ type, marketplaceOffer, setMarketplaceOffer });

    const getPlanIdentity = (plan: any) => plan?._id ?? plan?.id ?? null;

    const buildUpdatedMarketplaceOffer = (plans: any[]) => {
        const entityTypeName = marketplaceOffer._type || (typeof type === 'string' ? type : type?.name);
        if (!entityTypeName) {
            console.error('Cannot determine entity type for marketplace offer');
            return null;
        }

        const entityType = getEntityType(entityTypeName);
        return new entityType.type.type({
            ...marketplaceOffer,
            plans,
        });
    };

    const handleDeletePlan = async (plan: any) => {
        const targetId = getPlanIdentity(plan);
        const updatedPlans = marketplaceOffer.plans.filter((p: any) => {
            const currentId = getPlanIdentity(p);
            if (targetId && currentId) {
                return currentId.toString() !== targetId.toString();
            }
            return p !== plan;
        });

        const updatedOffer = buildUpdatedMarketplaceOffer(updatedPlans);
        if (!updatedOffer) {
            return;
        }

        setMarketplaceOffer(updatedOffer);

        if (updatedOffer._id && authentication?.customer) {
            try {
                await userAPI.updateMarketplaceOffer(authentication.customer, updatedOffer);
            } catch (error: any) {
                pushError(error);
            }
        }
    };

    // Inline voucher action handlers for custom plans
    // A plan without a customer has no voucher — say so instead of doing nothing on the click.
    const canIssueVoucher = (plan: any): boolean => {
        if (!marketplaceOffer._id || !authentication?.customer) return false;
        if (!plan.customer) {
            showMessageDialog(
                t('ui.voucherNoCustomer', 'No customer'),
                t('ui.voucherNoCustomerMessage', 'Enter the customer (customer ID or contact email) on this plan first — the voucher is issued for them.')
            );
            return false;
        }
        return true;
    };

    // The voucher is signed by the server first; the clipboard write starts inside the click
    // (copyTextWhenReady) so Safari does not refuse it. If the browser refuses anyway, the text is
    // shown for copying by hand.
    const copyVoucherText = async (plan: any, toText: (voucherToken: string) => string, copiedTitle: string, copiedMessage: string, manualTitle: string) => {
        const planId = plan.id || plan.name;
        setLoadingVoucherForPlan(planId);
        const offerId = marketplaceOffer._id as string; // checked in canIssueVoucher
        const text = userAPI
            .createMarketplaceOfferVoucher(authentication.customer, offerId, plan.customer, isEmailFormat(plan.customer), plan)
            .then(toText);
        try {
            await copyTextWhenReady(text);
            showMessageDialog(copiedTitle, copiedMessage);
        } catch (error: any) {
            if (error instanceof ClipboardRefusedError) {
                showMessageDialog(manualTitle, error.text);
            } else {
                pushError(error);
            }
        } finally {
            setLoadingVoucherForPlan(null);
        }
    };

    const handleInlineCopyVoucher = async (plan: any) => {
        if (!canIssueVoucher(plan)) return;
        await copyVoucherText(
            plan,
            (voucherToken) => voucherToken,
            t('ui.voucherCopied', 'Voucher copied'),
            t('ui.voucherCopiedToClipboard', 'The voucher code has been copied to your clipboard.'),
            t('ui.voucherCopyManually', 'Copy the voucher code')
        );
    };

    const handleInlineCopyVoucherLink = async (plan: any) => {
        if (!canIssueVoucher(plan)) return;
        await copyVoucherText(
            plan,
            (voucherToken) => `https://www.real-gain.org/marketplace/${marketplaceOffer._id}?voucher=${voucherToken}`,
            t('ui.linkCopied', 'Link copied'),
            t('ui.voucherLinkCopiedToClipboard', 'The voucher link has been copied to your clipboard.'),
            t('ui.voucherLinkCopyManually', 'Copy the voucher link')
        );
    };

    const handleInlineEmailVoucher = async (plan: any) => {
        if (!canIssueVoucher(plan)) return;

        const planId = plan.id || plan.name;
        setLoadingVoucherForPlan(planId);
        try {
            const isEmail = isEmailFormat(plan.customer);
            await userAPI.sendMarketplaceOfferVoucherEmail(
                authentication.customer,
                marketplaceOffer._id as string, // checked in canIssueVoucher
                plan.customer,
                isEmail,
                plan
            );
            showMessageDialog(t('ui.voucherEmailSent', 'Voucher email sent'), t('ui.voucherEmailSentMessage', 'The voucher has been sent to the customer.'));
        } catch (error: any) {
            pushError(error);
        } finally {
            setLoadingVoucherForPlan(null);
        }
    };

    const billingModelOptions = billingModelOptionsFor(t);
    const selectedBillingModelKey = selectedPlan?.billings?.[0]?.algorithm || 'perNamedUser';
    const selectedBillingModelText = billingModelOptions.find((option) => option.key === selectedBillingModelKey)?.text
        ?? String(selectedBillingModelKey);

    const planColumns: ColumnDef<any>[] = [{
        id: 'delete',
        header: '',
        size: 30,
        cell: ({ row }) => (
            <div onClick={(e) => e.stopPropagation()}>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 max-sm:h-11 max-sm:w-11"
                    aria-label={t('ui.delete')}
                    onClick={async (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        await handleDeletePlan(row.original);
                    }}
                >
                    <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
            </div>
        )
    }, {
        id: 'name',
        header: plainHeader(t('ui.name')),
        size: 150,
        cell: ({ row }) => {
            const plan: any = row.original;

            return (
                <div className="py-2">
                    <div className="flex min-w-0 items-center gap-1">
                        {plan.customer && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">C</span>
                                    </TooltipTrigger>
                                    <TooltipContent>{t('ui.customPlan', 'Custom plan for specific customer')}</TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                        <span className="min-w-0 break-words text-sm">{plan.name}</span>
                        {plan.description && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className="inline-flex shrink-0 cursor-default">
                                            <Info className="h-3.5 w-3.5 text-muted-foreground" aria-label={t('ui.description')} />
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent>{plan.description}</TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>
                    {plan.customer && (
                        <div className="mt-1 break-all text-sm text-muted-foreground">
                            {plan.customer}
                        </div>
                    )}
                </div>
            );
        }
    }, {
        id: 'baseFee',
        header: rightHeader(t('ui.baseFee')),
        size: 100,
        cell: ({ row }) => <div className="text-right text-sm">{(row.original.baseFee || 0).toFixed(2)} €</div>
    }, {
        id: 'billingModel',
        header: plainHeader(t('ui.billingModel')),
        size: 100,
        cell: ({ row }) => {
            return <div className="text-sm">{billingModelLabel(row.original.billings?.[0]?.algorithm, t)}</div>;
        }
    }, {
        id: 'included',
        header: rightHeader(t('ui.included')),
        size: 80,
        cell: ({ row }) => {
            return <div className="text-right text-sm">{row.original.billings?.[0]?.offset || 0}</div>;
        }
    }, {
        id: 'additional',
        header: rightHeader(t('ui.additional')),
        size: 80,
        cell: ({ row }) => {
            return <div className="text-right text-sm">{(row.original.billings?.[0]?.fee || 0).toFixed(2)} €</div>;
        }
    }, {
        id: 'term',
        header: plainHeader(t('ui.term')),
        size: 100,
        cell: ({ row }) => <div className="text-sm">{row.original.term} {t('ui.months')}</div>
    }, {
        id: 'validFrom',
        header: plainHeader(t('ui.validFrom')),
        size: 80,
        cell: ({ row }) => <div className="text-sm">{row.original.validFromDate ? dayjs(row.original.validFromDate).format('DD.MM.YY') : '-'}</div>
    }, {
        id: 'validUntil',
        header: plainHeader(t('ui.validUntil')),
        size: 80,
        cell: ({ row }) => <div className="text-sm">{row.original.validToDate ? dayjs(row.original.validToDate).format('DD.MM.YY') : '-'}</div>
    }, {
        id: 'voucher',
        header: plainHeader(t('ui.voucher', 'Voucher')),
        size: 100,
        cell: ({ row }) => {
            const plan: any = row.original;
            if (!plan.customer) return null;

            const planId = plan.id || plan.name;
            const isLoading = loadingVoucherForPlan === planId;

            if (isLoading) {
                return <Loader2 className="h-4 w-4 animate-spin" aria-hidden />;
            }

            return (
                <div className="flex items-center gap-1">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 max-sm:h-11 max-sm:w-11"
                                    aria-label={t('ui.copyVoucherCode', 'Copy voucher code')}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleInlineCopyVoucher(plan);
                                    }}
                                >
                                    <Copy className="h-3.5 w-3.5" aria-hidden />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t('ui.copyVoucherCode', 'Copy voucher code')}</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 max-sm:h-11 max-sm:w-11"
                                    aria-label={t('ui.copyVoucherLink', 'Copy voucher link')}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleInlineCopyVoucherLink(plan);
                                    }}
                                >
                                    <Link2 className="h-3.5 w-3.5" aria-hidden />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t('ui.copyVoucherLink', 'Copy voucher link')}</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 max-sm:h-11 max-sm:w-11"
                                    aria-label={t('ui.emailVoucher', 'Email voucher to customer')}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleInlineEmailVoucher(plan);
                                    }}
                                >
                                    <Mail className="h-3.5 w-3.5" aria-hidden />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t('ui.emailVoucher', 'Email voucher to customer')}</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            );
        }
    }];

    // Filter function: matches beginning of any word in name or description (case-insensitive)
    const filterPlans = (plans: any[], filter: string): any[] => {
        if (!filter.trim()) {
            return plans;
        }

        const filterLower = filter.toLowerCase();

        return plans.filter((plan) => {
            const name = (plan.name || '').toLowerCase();
            const description = (plan.description || '').toLowerCase();

            // Split into words and check if filter matches beginning of any word
            const nameWords = name.split(/\s+/);
            const descriptionWords = description.split(/\s+/);

            const nameMatches = nameWords.some((word: string) => word.startsWith(filterLower));
            const descriptionMatches = descriptionWords.some((word: string) => word.startsWith(filterLower));

            return nameMatches || descriptionMatches;
        });
    };

    const filteredPlans = filterPlans(marketplaceOffer.plans, filterText);
    const validFromDate = selectedPlan?.validFromDate ? new Date(selectedPlan.validFromDate) : undefined;
    const validFromSelected = validFromDate && !Number.isNaN(validFromDate.getTime()) ? validFromDate : undefined;

    return (
        <>
            <div className="mt-4 text-base font-semibold">{t('ui.billing')}</div>
            <div className="mt-4 flex flex-wrap items-center gap-4">
                <Button
                    type="button"
                    variant="ghost"
                    onClick={() => openPlanDialog()}
                >
                    <Plus className="mr-2 h-4 w-4" aria-hidden />
                    {t('ui.addPlan')}
                </Button>
                <div className="relative w-[300px] max-w-full">
                    <Filter className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                    <Input
                        className="pl-9"
                        aria-label={t('ui.filterPlans', 'Filter plans...')}
                        placeholder={t('ui.filterPlans', 'Filter plans...')}
                        value={filterText}
                        onChange={(event) => setFilterText(event.target.value || '')}
                    />
                </div>
            </div>
            <div className='mt-4 overflow-x-auto'>
                <DataTable
                    data={filteredPlans}
                    columns={planColumns}
                    onRowClick={(row) => openPlanDialog(row)}
                />
            </div>
            <Dialog
                open={planDialogOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        closePlanDialog();
                    }
                }}
            >
                <DialogContent
                    className="max-h-[85vh] max-w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[800px]"
                    onInteractOutside={(event) => event.preventDefault()}
                    onEscapeKeyDown={(event) => event.preventDefault()}
                >
                    <DialogHeader>
                        <DialogTitle>{t('ui.editPlan')}</DialogTitle>
                    </DialogHeader>
                    <div>
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor={`${fieldId}-customerCode`}>
                                {t('ui.customerIdOrContactEmail', 'Customer ID or contact e-mail')}
                            </Label>
                            <Input
                                id={`${fieldId}-customerCode`}
                                value={customerCode}
                                placeholder={t('ui.enterCustomerIdOrEmail', 'Enter customer ID or contact e-mail')}
                                aria-invalid={customerCodeError ? true : undefined}
                                className={cn(customerCodeError && 'border-destructive')}
                                onChange={(event) => handleCustomerCodeChange(event, event.target.value)}
                            />
                            {customerCodeError ? (
                                <p className="text-sm text-destructive">{customerCodeError}</p>
                            ) : null}
                        </div>
                        {customerDetails && (
                            <div className="mt-4">
                                <div className="text-sm">
                                    <div className="text-sm font-bold">{customerDetails.name}</div>
                                    {customerDetails.address && (
                                        <div className="mt-3">
                                            {customerDetails.address.street && customerDetails.address.streetNumber && (
                                                <div>{customerDetails.address.street} {customerDetails.address.streetNumber}</div>
                                            )}
                                            {customerDetails.address.postalCode && customerDetails.address.city && (
                                                <div>{customerDetails.address.postalCode} {customerDetails.address.city}</div>
                                            )}
                                            {customerDetails.address.country && (
                                                <div>{customerDetails.address.country}</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="mt-4 flex flex-col gap-1.5">
                            <Label htmlFor={`${fieldId}-billingModel`}>{t('ui.billingModel')}</Label>
                            <Popover open={billingModelOpen} onOpenChange={setBillingModelOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        id={`${fieldId}-billingModel`}
                                        type="button"
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={billingModelOpen}
                                        className="w-full justify-between font-normal"
                                    >
                                        <span className="truncate">{selectedBillingModelText}</span>
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                                    <Command>
                                        <CommandList>
                                            <CommandEmpty>{t('settingsPanel.dataTable.noRecordsText')}</CommandEmpty>
                                            <CommandGroup>
                                                {billingModelOptions.map((option) => (
                                                    <CommandItem
                                                        key={option.key}
                                                        value={option.text}
                                                        onSelect={() => {
                                                            handleBillingModelChange(null, option);
                                                            setBillingModelOpen(false);
                                                        }}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                'mr-2 h-4 w-4',
                                                                selectedBillingModelKey === option.key ? 'opacity-100' : 'opacity-0'
                                                            )}
                                                            aria-hidden
                                                        />
                                                        <span className="flex-1 truncate">{option.text}</span>
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div className="mt-4 flex flex-col gap-1.5">
                            <Label htmlFor={`${fieldId}-planName`}>{t('ui.name')}</Label>
                            <Input
                                id={`${fieldId}-planName`}
                                value={selectedPlan?.name || ''}
                                onChange={(event) => handleNameChange(event, event.target.value)}
                            />
                        </div>
                        <div className="mt-4 flex flex-col gap-1.5">
                            <Label htmlFor={`${fieldId}-planDescription`}>{t('ui.description')}</Label>
                            <Textarea
                                id={`${fieldId}-planDescription`}
                                rows={3}
                                value={selectedPlan?.description || ''}
                                onChange={(event) => handleDescriptionChange(event, event.target.value)}
                            />
                        </div>
                        <div className="mt-4 flex flex-wrap gap-4">
                            <NumberField
                                label={t('ui.baseFee')}
                                decimal
                                min={0}
                                step={0.01}
                                value={selectedPlan?.baseFee}
                                onChange={handleBaseFeeChange}
                                suffix="€"
                            />
                            {isUsageMeter(selectedPlan?.billings?.[0]?.algorithm) ? (
                                <>
                                    <NumberField
                                        label={includedLabel(selectedPlan?.billings?.[0]?.algorithm, t)}
                                        min={minimumIncluded(selectedPlan?.billings?.[0]?.algorithm)}
                                        step={1}
                                        value={selectedPlan?.billings?.[0]?.offset}
                                        onChange={handleIncludedRequestsChange}
                                    />
                                    <NumberField
                                        label={additionalFeeLabel(selectedPlan?.billings?.[0]?.algorithm, t)}
                                        decimal
                                        fixedDecimals={2}
                                        min={0}
                                        step={0.01}
                                        value={selectedPlan?.billings?.[0]?.fee}
                                        onChange={handleFeePerAdditionalRequestChange}
                                        suffix="€"
                                    />
                                </>
                            ) : (
                                <>
                                    <NumberField
                                        label={t('ui.includedUsers')}
                                        min={1}
                                        step={1}
                                        value={selectedPlan?.billings?.[0]?.offset}
                                        onChange={handleIncludedUsersChange}
                                    />
                                    <NumberField
                                        label={t('ui.monthlyFeeAdditionalUsers')}
                                        decimal
                                        fixedDecimals={2}
                                        min={0}
                                        step={0.01}
                                        value={selectedPlan?.billings?.[0]?.fee}
                                        onChange={handleMonthlyFeeChange}
                                        suffix="€"
                                    />
                                </>
                            )}
                        </div>
                        <div className="mt-4 text-sm text-primary">{t('ui.baseFeeBilledAnnually')}</div>
                        <div className="mt-4 flex flex-wrap items-end gap-4">
                            <div className="flex w-[250px] max-w-full flex-col gap-1.5">
                                <Label htmlFor={`${fieldId}-validFrom`}>{t('ui.validFrom')}</Label>
                                <Popover open={validFromOpen} onOpenChange={setValidFromOpen}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            id={`${fieldId}-validFrom`}
                                            type="button"
                                            variant="outline"
                                            className="w-full justify-between font-normal"
                                        >
                                            <span className={cn('truncate', !validFromSelected && 'text-muted-foreground')}>
                                                {validFromSelected ? dayjs(validFromSelected).format('DD.MM.YY') : t('ui.selectDate')}
                                            </span>
                                            <CalendarIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={validFromSelected}
                                            defaultMonth={validFromSelected}
                                            onSelect={(date) => {
                                                handleValidFromChange(date ?? null);
                                                setValidFromOpen(false);
                                            }}
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div className="pb-2">
                                <div className="text-sm text-muted-foreground">{t('ui.validUntil')}</div>
                                <div className="text-sm">
                                    {selectedPlan?.validFromDate
                                        ? dayjs(selectedPlan.validFromDate).add(1, 'year').subtract(1, 'day').format('DD.MM.YY')
                                        : '-'}
                                </div>
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="gap-2 pt-4 sm:gap-0">
                        <Button type="button" variant="outline" onClick={closePlanDialog}>{t('global.close')}</Button>
                        <Button type="button" onClick={async () => {
                            try {
                                await savePlan();
                            } catch (error: any) {
                                pushError(error);
                            }
                        }}>{t('global.save')}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Voucher Dialog - shown after plan is saved with a customer code */}
            <Dialog
                open={voucherDialogOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        closeVoucherDialog();
                    }
                }}
            >
                <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-[600px]">
                    <DialogHeader>
                        <DialogTitle>{t('ui.voucher', 'Voucher')}</DialogTitle>
                    </DialogHeader>
                    <div>
                        {savedPlanForVoucher && (
                            <div className="mb-4">
                                <div className="text-sm text-muted-foreground">{t('ui.planSavedForCustomer', 'Plan saved for')}:</div>
                                <div className="break-all text-sm font-bold">{(savedPlanForVoucher as any).customer}</div>
                                <div className="mt-3 text-sm">{savedPlanForVoucher.name}</div>
                            </div>
                        )}

                        {loadingVoucher ? (
                            <div className="text-sm">{t('ui.creatingVoucher', 'Creating voucher...')}</div>
                        ) : voucher ? (
                            <div>
                                <div className="mb-2 text-sm text-muted-foreground">{t('ui.voucherCode', 'Voucher code')}:</div>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 break-all rounded-md bg-muted p-2 text-xs text-muted-foreground">
                                        {voucher}
                                    </div>
                                </div>
                                <div className="mt-4 flex flex-wrap gap-4">
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button type="button" variant="outline" onClick={handleCopyVoucher}>
                                                    <Copy className="mr-2 h-4 w-4" aria-hidden />
                                                    {t('ui.copyCode', 'Copy Code')}
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>{t('ui.copyToClipboard', 'Copy to clipboard')}</TooltipContent>
                                        </Tooltip>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button type="button" variant="outline" onClick={handleCopyVoucherLink}>
                                                    <Link2 className="mr-2 h-4 w-4" aria-hidden />
                                                    {t('ui.copyLink', 'Copy Link')}
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>{t('ui.copyVoucherLink', 'Copy voucher link')}</TooltipContent>
                                        </Tooltip>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    type="button"
                                                    onClick={async () => {
                                                        try {
                                                            await handleEmailVoucher();
                                                            showMessageDialog(
                                                                t('ui.voucherEmailSent', 'Voucher email sent successfully'),
                                                                t('ui.voucherEmailSentMessage', 'The voucher has been sent to the customer.')
                                                            );
                                                            closeVoucherDialog();
                                                        } catch (error: any) {
                                                            pushError(error);
                                                        }
                                                    }}
                                                >
                                                    <Mail className="mr-2 h-4 w-4" aria-hidden />
                                                    {t('ui.sendEmail', 'Send Email')}
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>{t('ui.emailToCustomer', 'E-mail to customer')}</TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            </div>
                        ) : (
                            <div className="text-sm text-destructive">{t('ui.voucherCreationFailed', 'Failed to create voucher')}</div>
                        )}
                    </div>
                    <DialogFooter className="gap-2 pt-4 sm:gap-0">
                        <Button type="button" variant="outline" onClick={closeVoucherDialog}>{t('global.close')}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
